import { AlertTriangle, CheckCircle, DollarSign, Shuffle } from 'lucide-react'
import { Button } from '../ui/Button'
import { cn } from '../../lib/utils'
import type { IdentifiedProperty } from './IdentificationPropertyRow'

interface DstAllocationSummaryProps {
  properties: IdentifiedProperty[]
  salesPrice: number | null
  idRule: string
  disabled: boolean
  onDistributeEvenly: () => void
}

const TOLERANCE = 1

function formatDollars(value: number): string {
  return '$' + value.toLocaleString('en-US', { maximumFractionDigits: 0 })
}

export function DstAllocationSummary({
  properties,
  salesPrice,
  idRule,
  disabled,
  onDistributeEvenly,
}: DstAllocationSummaryProps) {
  if (!salesPrice || salesPrice <= 0 || properties.length === 0) return null

  const totalAllocated = properties.reduce(
    (sum, p) => sum + (p.allocationAmount ?? 0),
    0
  )
  const difference = Math.abs(totalAllocated - salesPrice)
  const balanced = difference <= TOLERANCE

  return (
    <div
      className={cn(
        'rounded-lg border px-4 py-3 space-y-2',
        balanced
          ? 'border-green-200 dark:border-green-800 bg-green-50 dark:bg-green-900/20'
          : 'border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/20'
      )}
      role="status"
      aria-label="DST allocation summary"
    >
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-start gap-2">
          {balanced ? (
            <CheckCircle
              className="h-4 w-4 text-green-600 dark:text-green-400 flex-shrink-0 mt-0.5"
              aria-hidden="true"
            />
          ) : (
            <AlertTriangle
              className="h-4 w-4 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5"
              aria-hidden="true"
            />
          )}
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span
                className={cn(
                  'text-xs font-semibold',
                  balanced
                    ? 'text-green-800 dark:text-green-200'
                    : 'text-amber-800 dark:text-amber-200'
                )}
              >
                Total allocated
              </span>
              <span
                className={cn(
                  'text-sm font-bold',
                  balanced
                    ? 'text-green-700 dark:text-green-300'
                    : 'text-amber-700 dark:text-amber-300'
                )}
              >
                {formatDollars(totalAllocated)}
                <span
                  className={cn(
                    'text-xs font-normal ml-1',
                    balanced
                      ? 'text-green-700 dark:text-green-300'
                      : 'text-amber-700 dark:text-amber-300'
                  )}
                >
                  / Required: {formatDollars(salesPrice)}
                </span>
              </span>
            </div>
            {!balanced && (
              <p
                className="text-xs text-amber-700 dark:text-amber-300 mt-0.5"
                role="alert"
                aria-live="polite"
              >
                {totalAllocated < salesPrice
                  ? `${formatDollars(salesPrice - totalAllocated)} unallocated`
                  : `${formatDollars(totalAllocated - salesPrice)} over sales price`}
              </p>
            )}
            {balanced && (
              <p className="text-xs text-green-700 dark:text-green-300 mt-0.5">
                Allocations match sales price
              </p>
            )}
          </div>
        </div>

        {idRule === '3-property' && !disabled && properties.length > 0 && (
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={onDistributeEvenly}
            disabled={disabled || !salesPrice}
            aria-label="Distribute sales price evenly across all properties"
            className="flex-shrink-0 h-7 px-2 text-xs border-amber-300 dark:border-amber-700 text-amber-700 dark:text-amber-300 hover:bg-amber-100 dark:hover:bg-amber-900/30"
          >
            <Shuffle className="h-3 w-3 mr-1" aria-hidden="true" />
            <DollarSign className="h-3 w-3" aria-hidden="true" />
            Distribute evenly
          </Button>
        )}
      </div>
    </div>
  )
}
